import { useMemo, useState } from "react";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import ChartCard from "../widgets/graphs/ChartCard.tsx";
import { formatNumber } from "../widgets/graphs/chartTheme.ts";
import {
  Reporte,
  TiemposCiclo,
  TramoTiempo,
  formatDias,
} from "./reportesUtils.ts";

/* ------------------------------------------------------------------ */
/* Stage definitions (codes from /reports/ciclo/tiempos)                */
/* ------------------------------------------------------------------ */

export interface EtapaCiclo {
  codigo: string;
  titulo: string;
}

export interface DefinicionCiclo {
  etapas: EtapaCiclo[];
  // End-to-end measure shown as the headline (measured, not a sum)
  total: { codigo: string; titulo: string };
  // Milestones shown as the process path above the lanes
  hitos: string[];
}

// Each stage is measured on its own (different case counts), so stages are
// shown side by side and never added up. "Recepción -> factura" is left out:
// invoices usually arrive before the reception, so most cases are discarded
export const CICLO_SOLICITUD: DefinicionCiclo = {
  etapas: [
    { codigo: "solicitudAAprobacion", titulo: "Aprobación de la solicitud" },
    { codigo: "aprobacionSolicitudAOrden", titulo: "Generación de la OC" },
    { codigo: "ordenAEmision", titulo: "Ingreso a INET" },
    { codigo: "emisionARecepcion", titulo: "Recepción" },
    { codigo: "ordenAFactura", titulo: "Facturación" },
    { codigo: "facturaAContabilizacion", titulo: "Contabilización" },
  ],
  total: {
    codigo: "cicloCompleto",
    titulo: "Ciclo completo · solicitud → contabilización",
  },
  hitos: [
    "Solicitud",
    "Aprobación",
    "OC",
    "INET",
    "Recepción",
    "Factura",
    "Contabilización",
  ],
};

export const CICLO_ORDEN: DefinicionCiclo = {
  etapas: [
    { codigo: "ordenAAprobacion", titulo: "Aprobación de la OC" },
    { codigo: "aprobacionAConfirmacion", titulo: "Confirmación" },
    { codigo: "ordenAEmision", titulo: "Ingreso a INET" },
    { codigo: "emisionARecepcion", titulo: "Recepción" },
    { codigo: "ordenAFactura", titulo: "Facturación" },
    { codigo: "facturaAContabilizacion", titulo: "Contabilización" },
  ],
  total: {
    codigo: "ordenARecepcion",
    titulo: "Creación de la OC → primera recepción",
  },
  hitos: [
    "OC",
    "Aprobación",
    "Confirmación",
    "INET",
    "Recepción",
    "Factura",
    "Contabilización",
  ],
};

// Codes of a cycle (for the Excel export)
export const codigosCiclo = (def: DefinicionCiclo) => [
  ...def.etapas.map((e) => e.codigo),
  def.total.codigo,
];

/* ------------------------------------------------------------------ */
/* Card                                                                 */
/* ------------------------------------------------------------------ */

// Emphasis form: every stage in gray, the slowest one in the accent hue
// (validated pair: normal-vision dE 15.1, CVD dE 12.9; values are printed as
// text and a table view exists, which covers the gray's 2.9:1 contrast)
const COLOR_ETAPA = "#8a97a8";
const COLOR_ETAPA_P90 = "#d5dbe3";
const COLOR_DEMORA = "#2f7ed8";
const COLOR_DEMORA_P90 = "#b3cff2";

// Typical stages stay readable next to a very long tail: the track is capped
// at this multiple of the largest median; longer p90 values get an arrow
const ESCALA_MAX_MEDIANAS = 2.5;

const VISTA_OPTIONS = [
  { label: "Gráfico", value: "grafico" },
  { label: "Tabla", value: "tabla" },
];

interface Fila extends EtapaCiclo {
  tramo: TramoTiempo | null;
}

interface CicloVidaCardProps {
  title: string;
  subtitle?: string;
  reporte: Reporte<TiemposCiclo>;
  definicion: DefinicionCiclo;
}

/**
 * Life cycle as a process lane: one row per stage in process order, a solid
 * bar to the median ("típico") and a lighter extension to the p90 ("9 de
 * 10"), the slowest stage emphasized, and the measured end-to-end time.
 */
export default function CicloVidaCard({
  title,
  subtitle,
  reporte,
  definicion,
}: CicloVidaCardProps) {
  const [vista, setVista] = useState("grafico");

  const datos = useMemo(() => {
    const tramos = reporte.data?.tramos ?? [];
    const filas: Fila[] = definicion.etapas.map((e) => ({
      ...e,
      tramo: tramos.find((t) => t.codigo === e.codigo) ?? null,
    }));
    const conDatos = filas.filter((f) => f.tramo && f.tramo.cantidad > 0);
    const mediana = (f: Fila) => f.tramo?.mediana ?? 0;
    const p90 = (f: Fila) => f.tramo?.p90 ?? 0;
    const maxMediana = Math.max(0, ...conDatos.map(mediana));
    const maxP90 = Math.max(0, ...conDatos.map(p90));
    const escala = Math.max(
      Math.min(maxP90, maxMediana * ESCALA_MAX_MEDIANAS),
      maxMediana * 1.1,
      1,
    );
    const demora = conDatos.length
      ? conDatos.reduce((a, b) => (mediana(b) > mediana(a) ? b : a))
      : null;
    return {
      filas,
      escala,
      recortada: maxP90 > escala,
      demora: demora && mediana(demora) > 0 ? demora : null,
      total: tramos.find((t) => t.codigo === definicion.total.codigo) ?? null,
    };
  }, [reporte.data, definicion]);

  const pct = (dias: number | null | undefined) =>
    `${Math.max(0, Math.min(100, ((dias ?? 0) / datos.escala) * 100))}%`;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * datos.escala);
  const total = datos.total;

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      loading={reporte.loading}
      error={reporte.error}
      onRetry={reporte.reload}
      empty={!datos.filas.some((f) => f.tramo && f.tramo.cantidad > 0)}
      height={320}
      actions={
        <SelectButton
          value={vista}
          options={VISTA_OPTIONS}
          onChange={(e) => e.value && setVista(e.value)}
          className="fd-select-sm"
          allowEmpty={false}
        />
      }
    >
      {/* Headline: measured end-to-end time + the slowest stage */}
      <div className="cv-resumen">
        <div className="cv-hero">
          <span className="cv-hero-valor">
            {total?.mediana !== null && total?.mediana !== undefined
              ? formatDias(total.mediana)
              : "-"}
          </span>
          <span className="cv-hero-texto">
            <strong>{definicion.total.titulo}</strong>
            <span>
              Mediana medida de punta a punta
              {total
                ? ` · 9 de 10 en ≤ ${formatDias(total.p90)} · ${formatNumber(
                    total.cantidad,
                  )} casos`
                : ""}
            </span>
          </span>
        </div>
        {datos.demora ? (
          <div className="cv-demora">
            <i className="pi pi-flag-fill" aria-hidden="true" />
            <span>
              Mayor demora: <strong>{datos.demora.titulo}</strong> ·{" "}
              {formatDias(datos.demora.tramo?.mediana)} típicos
            </span>
          </div>
        ) : null}
      </div>

      <ol className="cv-hitos" aria-label="Etapas del proceso">
        {definicion.hitos.map((h) => (
          <li key={h}>{h}</li>
        ))}
      </ol>

      {vista === "tabla" ? (
        <DataTable
          value={datos.filas.filter((f) => f.tramo)}
          size="small"
          stripedRows
          className="fd-table"
        >
          <Column header="Etapa" field="titulo" />
          <Column header="Desde → hasta" body={(f: Fila) => f.tramo?.nombre} />
          <Column
            header="Mediana"
            body={(f: Fila) => (
              <span className="fd-num">{formatDias(f.tramo?.mediana)}</span>
            )}
          />
          <Column
            header="Promedio"
            body={(f: Fila) => (
              <span className="fd-num">{formatDias(f.tramo?.promedio)}</span>
            )}
          />
          <Column
            header="P90"
            body={(f: Fila) => (
              <span className="fd-num">{formatDias(f.tramo?.p90)}</span>
            )}
          />
          <Column
            header="Mín / Máx"
            body={(f: Fila) => (
              <span className="fd-num">
                {formatDias(f.tramo?.minimo)} / {formatDias(f.tramo?.maximo)}
              </span>
            )}
          />
          <Column
            header="Casos"
            body={(f: Fila) => (
              <span className="fd-num">{formatNumber(f.tramo?.cantidad)}</span>
            )}
          />
          <Column
            header="Descartados"
            headerTooltip="Casos sin fechas válidas para la etapa"
            body={(f: Fila) => (
              <span className="fd-num">
                {formatNumber(f.tramo?.descartados)}
              </span>
            )}
          />
        </DataTable>
      ) : (
        <div className="cv-carriles">
          <div className="cv-fila cv-encabezado" aria-hidden="true">
            <span />
            <span>Etapa</span>
            <span className="cv-escala">
              {ticks.map((t, i) => (
                <span key={i} style={{ left: pct(t) }}>
                  {formatDias(t)}
                </span>
              ))}
            </span>
            <span className="cv-num">Típico</span>
            <span className="cv-num">9 de 10</span>
            <span className="cv-num">Casos</span>
          </div>

          {datos.filas.map((f, i) => {
            const t = f.tramo;
            const esDemora = datos.demora?.codigo === f.codigo;
            const color = esDemora ? COLOR_DEMORA : COLOR_ETAPA;
            const colorP90 = esDemora ? COLOR_DEMORA_P90 : COLOR_ETAPA_P90;
            const sinDatos = !t || t.cantidad === 0;
            const p90Recortado = !!t?.p90 && t.p90 > datos.escala;
            const detalle = t
              ? `${t.nombre}\nMediana ${formatDias(t.mediana)} · promedio ${formatDias(
                  t.promedio,
                )} · p90 ${formatDias(t.p90)}\nMín ${formatDias(
                  t.minimo,
                )} · máx ${formatDias(t.maximo)} · ${formatNumber(
                  t.cantidad,
                )} casos`
              : "Sin datos para esta etapa";
            return (
              <div
                key={f.codigo}
                className={`cv-fila${esDemora ? " cv-fila-demora" : ""}`}
                title={detalle}
              >
                <span
                  className="cv-paso"
                  style={{
                    borderColor: color,
                    color: esDemora ? color : undefined,
                  }}
                >
                  {i + 1}
                </span>
                <span className="cv-etapa">
                  <span className="cv-etapa-titulo">
                    {f.titulo}
                    {esDemora ? (
                      <span className="cv-tag">Mayor demora</span>
                    ) : null}
                  </span>
                  <span className="cv-etapa-rango">{t?.nombre ?? "-"}</span>
                </span>
                <span className="cv-pista" aria-hidden="true">
                  {sinDatos ? null : (
                    <>
                      <span
                        className="cv-barra-p90"
                        style={{
                          left: 0,
                          width: pct(Math.min(t?.p90 ?? 0, datos.escala)),
                          backgroundColor: colorP90,
                        }}
                      />
                      <span
                        className="cv-barra"
                        style={{
                          width: pct(t?.mediana),
                          backgroundColor: color,
                        }}
                      />
                      {p90Recortado ? (
                        <i
                          className="pi pi-angle-double-right cv-flecha"
                          style={{ color }}
                        />
                      ) : null}
                    </>
                  )}
                </span>
                <span className="cv-num cv-valor">
                  {sinDatos ? "-" : formatDias(t?.mediana)}
                </span>
                <span className="cv-num cv-sub">
                  {sinDatos ? "-" : `≤ ${formatDias(t?.p90)}`}
                </span>
                <span className="cv-num cv-sub">
                  {sinDatos ? "-" : formatNumber(t?.cantidad)}
                </span>
              </div>
            );
          })}

          <p className="cv-nota">
            Barra sólida: mediana (tiempo típico) · tramo claro: hasta el
            percentil 90 (9 de cada 10 casos).
            {datos.recortada
              ? ` Escala recortada en ${formatDias(datos.escala)}: las etapas con flecha continúan.`
              : ""}{" "}
            Cada etapa se mide por separado, por eso no se suman; recepción y
            factura pueden ocurrir en cualquier orden.
          </p>
        </div>
      )}
    </ChartCard>
  );
}
